/**
 * SenderKeyDistributionMessage — bootstrap payload that lets a receiver
 * install a sender's group ratchet state.
 *
 * The SKDM is shipped INSIDE an existing pairwise E2EE session (same as
 * any chat message), so its content is implicitly authenticated by the
 * outer envelope's Ed25519 sender signature. We additionally include a
 * self-signature inside the SKDM payload: a buggy server that strips
 * the outer signature and replays the inner blob cannot fool us into
 * thinking we got an SKDM from someone we didn't, because the embedded
 * signature only verifies under the embedded `signingPublicKey` — and
 * future SenderKeyMessages will be signed by the matching private key,
 * so a swap would immediately break group decryption.
 *
 * ## Wire format of the *plaintext that gets encrypted*
 *
 *   kaether:skdm:v1:<base64url(JSON)>
 *
 * ## Canonical bytes for the embedded self-signature
 *
 *   schema(8BE) || 0x00 || groupId(utf8) || 0x00 || senderId(utf8) || 0x00 ||
 *   deviceId(8BE) || 0x00 || chainKey(raw) || 0x00 ||
 *   chainIndex(8BE) || 0x00 || signingPublicKey(raw) || 0x00 || ts(8BE)
 *
 * 8-byte big-endian fixed-length int encoding prevents a malicious peer
 * from reshaping the same logical content into a different byte string.
 *
 * ## iOS parity
 * Mirrors `kaether_for_ios/Stoat/E2EE/SenderKeyDistributionMessage.swift`
 * byte-for-byte. Prefix: `kaether:skdm:v1:`.
 *
 * Pure logic + SubtleCrypto only. No SolidJS imports.
 */

import {
  validateChainKeyLength,
  wrapEd25519SeedAsPkcs8,
  CHAIN_KEY_LENGTH,
  type SenderKeyState,
} from "./SenderKey";

// ─── types ────────────────────────────────────────────────────────────────────

export interface SenderKeyDistributionMessage {
  readonly schema: number;
  readonly groupId: string;
  readonly senderId: string;
  readonly deviceId: number;
  /** Raw 32-byte chain key, base64-encoded in JSON. */
  readonly chainKey: string;
  readonly chainIndex: number;
  /** Raw 32-byte Ed25519 public key, base64-encoded in JSON. */
  readonly signingPublicKey: string;
  /** Ed25519 signature over canonical bytes, base64-encoded in JSON. */
  readonly selfSignature: string;
  readonly ts: number;
}

const CURRENT_SCHEMA = 1;

// ─── errors ───────────────────────────────────────────────────────────────────

export class SKDMCodecError extends Error {
  constructor(
    public readonly kind:
      | "notAnSKDM"
      | "malformed"
      | "unsupportedSchema"
      | "invalidSelfSignature",
    detail: string,
  ) {
    super(`[SKDMCodec:${kind}] ${detail}`);
    this.name = "SKDMCodecError";
  }
}

// ─── base64url helpers ────────────────────────────────────────────────────────

function toBase64Url(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

function fromBase64Url(str: string): Uint8Array {
  let s = str.replace(/-/g, "+").replace(/_/g, "/");
  while (s.length % 4 !== 0) s += "=";
  const binary = atob(s);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

// Standard base64 (no url-safe) for JSON field values
function toBase64(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function fromBase64(str: string): Uint8Array {
  let s = str;
  while (s.length % 4 !== 0) s += "=";
  const binary = atob(s);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

// ─── canonical bytes ──────────────────────────────────────────────────────────

const enc = new TextEncoder();

/** Write an 8-byte big-endian int64 into a DataView at offset. */
function writeI64BE(view: DataView, offset: number, value: number): void {
  // JavaScript Numbers are IEEE-754 doubles; safe for integers up to 2^53.
  // Split into high and low 32-bit words.
  const high = Math.floor(value / 0x1_0000_0000);
  const low = value >>> 0;
  view.setUint32(offset, high >>> 0, false);
  view.setUint32(offset + 4, low, false);
}

/**
 * Produces the canonical byte string over which the SKDM self-signature
 * is computed. Must match iOS byte-for-byte.
 *
 *   schema(8BE) NUL groupId(utf8) NUL senderId(utf8) NUL
 *   deviceId(8BE) NUL chainKey(raw) NUL chainIndex(8BE) NUL
 *   signingPublicKey(raw) NUL ts(8BE)
 */
function skdmCanonicalBytes(
  schema: number,
  groupId: string,
  senderId: string,
  deviceId: number,
  chainKeyBytes: Uint8Array,
  chainIndex: number,
  signingPublicKeyBytes: Uint8Array,
  ts: number,
): Uint8Array {
  const groupIdBytes = enc.encode(groupId);
  const senderIdBytes = enc.encode(senderId);

  // Pre-compute total length:
  // 8 (schema) + 1 (NUL) + groupId + 1 + senderId + 1 +
  // 8 (deviceId) + 1 + 32 (chainKey) + 1 +
  // 8 (chainIndex) + 1 + 32 (sigPub) + 1 + 8 (ts)
  const totalLen =
    8 + 1 +
    groupIdBytes.byteLength + 1 +
    senderIdBytes.byteLength + 1 +
    8 + 1 +
    chainKeyBytes.byteLength + 1 +
    8 + 1 +
    signingPublicKeyBytes.byteLength + 1 +
    8;

  const buf = new Uint8Array(totalLen);
  const view = new DataView(buf.buffer);
  let pos = 0;

  writeI64BE(view, pos, schema);
  pos += 8;
  buf[pos++] = 0x00;

  buf.set(groupIdBytes, pos);
  pos += groupIdBytes.byteLength;
  buf[pos++] = 0x00;

  buf.set(senderIdBytes, pos);
  pos += senderIdBytes.byteLength;
  buf[pos++] = 0x00;

  writeI64BE(view, pos, deviceId);
  pos += 8;
  buf[pos++] = 0x00;

  buf.set(chainKeyBytes, pos);
  pos += chainKeyBytes.byteLength;
  buf[pos++] = 0x00;

  writeI64BE(view, pos, chainIndex);
  pos += 8;
  buf[pos++] = 0x00;

  buf.set(signingPublicKeyBytes, pos);
  pos += signingPublicKeyBytes.byteLength;
  buf[pos++] = 0x00;

  writeI64BE(view, pos, ts);

  return buf;
}

// ─── codec ────────────────────────────────────────────────────────────────────

export const SKDM_PREFIX = "kaether:skdm:v1:";

export function isSKDM(s: string): boolean {
  return s.startsWith(SKDM_PREFIX);
}

export function encodeSKDM(skdm: SenderKeyDistributionMessage): string {
  const json = JSON.stringify(skdm);
  const jsonBytes = enc.encode(json);
  return SKDM_PREFIX + toBase64Url(jsonBytes);
}

export async function decodeSKDM(
  s: string,
): Promise<SenderKeyDistributionMessage> {
  if (!s.startsWith(SKDM_PREFIX)) {
    throw new SKDMCodecError("notAnSKDM", "string does not start with kaether:skdm:v1:");
  }
  const b64 = s.slice(SKDM_PREFIX.length);
  let jsonBytes: Uint8Array;
  try {
    jsonBytes = fromBase64Url(b64);
  } catch {
    throw new SKDMCodecError("malformed", "invalid base64url payload");
  }

  let skdm: SenderKeyDistributionMessage;
  try {
    const text = new TextDecoder().decode(jsonBytes);
    skdm = JSON.parse(text) as SenderKeyDistributionMessage;
  } catch {
    throw new SKDMCodecError("malformed", "JSON parse failed");
  }

  if (skdm.schema !== CURRENT_SCHEMA) {
    throw new SKDMCodecError(
      "unsupportedSchema",
      `expected schema ${CURRENT_SCHEMA}, got ${skdm.schema}`,
    );
  }
  if (!skdm.groupId) {
    throw new SKDMCodecError("malformed", "empty groupId");
  }
  if (!skdm.senderId) {
    throw new SKDMCodecError("malformed", "empty senderId");
  }

  const chainKeyBytes = fromBase64(skdm.chainKey);
  if (chainKeyBytes.byteLength !== CHAIN_KEY_LENGTH) {
    throw new SKDMCodecError(
      "malformed",
      `invalid chainKey length: ${chainKeyBytes.byteLength}`,
    );
  }

  const signingPublicKeyBytes = fromBase64(skdm.signingPublicKey);
  const selfSignatureBytes = fromBase64(skdm.selfSignature);

  // Verify self-signature BEFORE the caller is allowed to act on any
  // field — a tampered chainKey would otherwise install a ratchet state
  // the sender didn't actually publish.
  const canonical = skdmCanonicalBytes(
    skdm.schema,
    skdm.groupId,
    skdm.senderId,
    skdm.deviceId,
    chainKeyBytes,
    skdm.chainIndex,
    signingPublicKeyBytes,
    skdm.ts,
  );

  let pubKey: CryptoKey;
  try {
    pubKey = await crypto.subtle.importKey(
      "raw",
      signingPublicKeyBytes,
      { name: "Ed25519" },
      false,
      ["verify"],
    );
  } catch {
    throw new SKDMCodecError("malformed", "failed to import signingPublicKey");
  }

  let valid: boolean;
  try {
    valid = await crypto.subtle.verify(
      { name: "Ed25519" },
      pubKey,
      selfSignatureBytes,
      canonical,
    );
  } catch {
    throw new SKDMCodecError("invalidSelfSignature", "verify threw");
  }

  if (!valid) {
    throw new SKDMCodecError("invalidSelfSignature", "self-signature did not verify");
  }

  return skdm;
}

/**
 * Convenience: returns null for non-SKDM strings.
 * Throws only for malformed payloads that *did* claim to be SKDMs.
 */
export async function tryDecodeSKDM(
  s: string,
): Promise<SenderKeyDistributionMessage | null> {
  if (!isSKDM(s)) return null;
  return decodeSKDM(s);
}

/**
 * Builds an SKDM from a locally-owned SenderKeyState. Requires
 * `state.signingPrivateKeyBytes != null` because we sign the canonical
 * bytes with it; receivers cannot construct SKDMs since their stored
 * state has the private key stripped.
 */
export async function buildSKDMFromLocal(
  state: SenderKeyState,
  groupId: string,
  senderId: string,
  deviceId: number,
  ts?: number,
): Promise<SenderKeyDistributionMessage> {
  if (state.signingPrivateKeyBytes === null) {
    throw new SKDMCodecError(
      "malformed",
      "buildSKDMFromLocal requires a signing private key",
    );
  }
  validateChainKeyLength(state.chainKeyBytes);

  const tsValue = ts ?? Math.floor(Date.now() / 1000);

  const canonical = skdmCanonicalBytes(
    CURRENT_SCHEMA,
    groupId,
    senderId,
    deviceId,
    state.chainKeyBytes,
    state.chainIndex,
    state.signingPublicKeyBytes,
    tsValue,
  );

  let privKey: CryptoKey;
  try {
    // Chrome rejects raw private-key import for Ed25519 — wrap in PKCS#8.
    privKey = await crypto.subtle.importKey(
      "pkcs8",
      wrapEd25519SeedAsPkcs8(state.signingPrivateKeyBytes),
      { name: "Ed25519" },
      false,
      ["sign"],
    );
  } catch {
    throw new SKDMCodecError("malformed", "failed to import signingPrivateKey");
  }

  const sigBuf = await crypto.subtle.sign({ name: "Ed25519" }, privKey, canonical);
  const selfSignatureBytes = new Uint8Array(sigBuf);

  return {
    schema: CURRENT_SCHEMA,
    groupId,
    senderId,
    deviceId,
    chainKey: toBase64(state.chainKeyBytes),
    chainIndex: state.chainIndex,
    signingPublicKey: toBase64(state.signingPublicKeyBytes),
    selfSignature: toBase64(selfSignatureBytes),
    ts: tsValue,
  };
}
